import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Toaster, toast } from "react-hot-toast";
import { useGlobalContext } from "../context/globalContext";
import axios from 'axios'
import React, { useEffect, useState } from "react";
import CommentModal from "./CommentModal";
import ChangeStatusModal from "./ChangeStatusModal";
import EditQueryModal from "./EditQueryModal";
import { se } from "date-fns/locale";

// eslint-disable-next-line react/prop-types
export default function TableRenderer({ data, columns, updateRow, getData, handleDelete, showEdit, defaultSortDesc = true }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [columnSizing, setColumnSizing] = useState({});
  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState("");
  const [selectedCellValue, setSelectedCellValue] = useState({});
  const [changeStatusModal, setChangeStatusModal] = useState(false);
  const { userInfo } = useGlobalContext();
  // console.log("data", data);

  const columnMapping = {
    id: "ID",
    date: "Date",
    name: "Name",
    new_name: "New Name",
    email: "Email",
    new_email: "New Email",
    number: "Number",
    new_number: "New Number",
    amount: "Amount",
    current_batch: "Current Batch",
    new_batch: "New Batch",
    current_course: "Current Course",
    new_course: "New Course",
    utr: "UTR", // ✅ new column label
    newCourseId: "New Course ID", // ✅ new column label
    student_user_id: "Student/User ID",
    content_desc: "Content Description",
    reason: "Reason",
    feedback: "Feedback",
    first_installment: "First Installment",
    second_installment: "Second Installment",
    file: "File",
    deviceName: "Device Name",
    deviceSpecs: "Device Specs",
    query_desc: "Issue",
    taken_by: "Taken By",
    comment: "Comment",
    status: "Status",
    actions: "Actions", // ✅ action column label
    "oc_answered": "OC Answered",
    "oc_missed": "OC Missed",
    "ic_missed": "IC Missed",
    "ic_answered": "IC Answered",
    "agent_name": "Agent Name",
    "total_calls": "Total Calls",
    "duration": "Duration in secs"

  };

  // Dynamic actions column definition
  const finalColumns = React.useMemo(() => {
    let cols = [...columns];
    if (handleDelete) {
      cols.push({
        id: "actions",
        header: "Actions",
        accessorKey: "actions",
        footer: "Actions",
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
            {showEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCellValue(row.original);
                  setSelectedRowId(row.original.id);
                  setIsEditModalOpen(true);
                }}
                className="text-blue-500 hover:text-blue-700"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}
                title="Edit Query"
              >
                <i className="fa-solid fa-pen-to-square" style={{ fontSize: '18px', color: '#3b82f6' }}></i>
              </button>
            )}
            {userInfo?.isAdmin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm("Are you sure you want to delete this query?")) {
                    handleDelete(row.original);
                  }
                }}
                className="text-red-500 hover:text-red-700"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}
                title="Delete Query"
              >
                <i className="fa-solid fa-trash-can" style={{ fontSize: '18px', color: '#ef4444' }}></i>
              </button>
            )}
          </div>
        )
      });
    }
    return cols;
  }, [columns, handleDelete, showEdit, userInfo]);

  const handleCellClick = (cell) => {
    {
      if (userInfo.isAdmin) {
        if (cell.column.id === "comment") {
          setSelectedCellValue(cell.row.original);
          setSelectedRowId(cell.row.original.id);
          setSelectedComment(cell.row.original.comment);
          setIsModalOpen(true);
        } else if (cell.column.id === "status") {
          setSelectedCellValue(cell.row.original);
          setSelectedRowId(cell.row.original.id);
          setCurrentStatus(cell.row.original.status);
          setChangeStatusModal(true);
        }
      } else {
        console.log("Not an Admin :(");
      }
    }
  };

const handleSaveComment = async (comment) => {
    toast.loading("Adding comment", selectedRowId);
    try {
      const updatePayload = {
        queryType: selectedCellValue.query_type, // You'll need to ensure you have access to the selected row's data
        id: selectedCellValue.id, // Assuming this is the unique identifier for the row
        timestamp: selectedCellValue.timestamp, // Pass timestamp for direct O(1) database lookup
        status: selectedCellValue.status,
        newStatus: selectedCellValue.status, // Assuming this is for unresolved queries
        isStatusChanged: false, // Just updating a comment, not changing status
        updatedFields: {
          comment: comment
        }
      };

      console.log('Comment update payload:', updatePayload);
      
      const response = await axios.put(
        "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/api/queries",
        updatePayload
      );
      console.log('Comment update response:', response.data);  
      // Update the front end directly
      updateRow(selectedRowId, { comment: comment });

      toast.dismiss();
      toast.success("Comment updated successfully!");
      setIsModalOpen(false);
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.error || error.message);
      console.error("Error updating comment:", error);
    }
  };

  const handleSaveQueryFields = async (updatedFieldsData) => {
    toast.loading("Updating query fields...", selectedRowId);
    try {
      // Exclude system keys from updatedFields
      const excludedKeys = [
        "id",
        "query_type",
        "status",
        "timestamp",
        "createdAt",
        "lastUpdated",
        "updatedBy",
        "original_id",
        "Status",
        "taken_by"
      ];
      
      const cleanUpdatedFields = {};
      Object.keys(updatedFieldsData).forEach(key => {
        if (!excludedKeys.includes(key)) {
          cleanUpdatedFields[key] = updatedFieldsData[key];
        }
      });

      const updatePayload = {
        queryType: selectedCellValue.query_type,
        id: selectedCellValue.id,
        timestamp: selectedCellValue.timestamp, // direct key read
        status: selectedCellValue.status,
        isStatusChanged: false,
        updatedFields: cleanUpdatedFields
      };

      console.log('Query update payload:', updatePayload);

      const response = await axios.put(
        "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/api/queries",
        updatePayload
      );

      console.log('Query update response:', response.data);

      updateRow(selectedRowId, cleanUpdatedFields);
      toast.dismiss();
      toast.success("Query updated successfully!");
      setIsEditModalOpen(false);
      getData({ withToast: false });
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.error || error.message);
      console.error("Error updating query:", error);
    }
  };

const handleChangeStatus = async (newStatus) => {
  toast.loading("Changing status", selectedCellValue?.id);
  newStatus = newStatus.toUpperCase();
  
  try {
      const updatePayload = {
          queryType: selectedCellValue.query_type,
          id: selectedCellValue.id,
          timestamp: selectedCellValue.timestamp, // Pass timestamp for direct O(1) database lookup
          status: selectedCellValue?.status || "PENDING", // current status
          isStatusChanged: true, // This is important - indicates we're changing status
          newStatus: newStatus, // The new status we're changing to
          updatedFields: {
              lastUpdated: new Date().toISOString(),
              updatedBy: userInfo?.email || 'unknown' // Optional: track who made the change
          }
      };

      console.log('Status update payload:', updatePayload);

      const response = await axios.put(
          "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/api/queries",
          updatePayload
      );

      console.log('Status update response:', response.data);

      // Update the front end directly
      updateRow(selectedCellValue?.id, { 
          status: newStatus,
          lastUpdated: new Date().toISOString(),
          updatedBy: userInfo?.email || 'unknown'
      });
      getData({withToast: false});
      toast.dismiss();
      toast.success("Status updated successfully!");
      setChangeStatusModal(false); // Close the modal if you're using one
      
  } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.error || error.message);
      console.error("Error updating status:", error);
  }
};

  const table = useReactTable({
    data,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: "onChange",
    state: {
      globalFilter: filtering,
      columnSizing,

      sorting,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
    onColumnSizingChange: setColumnSizing,
    initialState: {
      sorting: [
        {
          id: 'date',
          desc: defaultSortDesc, // sort direction by default
        },
      ],
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
  });
  useEffect(() => {
    // console.log("data", data);
    /*  
    data = [
    {
        "id": 1,
        "how are you ??": "hello "
    },
    {
        "id": 2,
        "how are you ??": "good"
    },...
]
    */
    // console.log("columns", columns);
    /*
    columns = [
    {
        "id": "date",
        "header": "date",
        "accessorKey": "date",
        "footer": "date"
    },
   {
    "id": "new_name",
    "header": "new_name",
    "accessorKey": "new_name",
    "footer": "new_name"
}
    ...
]
    */
  }, [data, columns]);

  return (
    <div className="table-elements-container">
      {isModalOpen && (
        <CommentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveComment}
          currComment={selectedComment}
        />
      )}
      {changeStatusModal && (
        <ChangeStatusModal
          onClose={() => setChangeStatusModal(false)}
          handleChangeStatus={handleChangeStatus}
          currentStatus={currentStatus}
        />
      )}
      {isEditModalOpen && (
        <EditQueryModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveQueryFields}
          rowData={selectedCellValue}
          columnMapping={columnMapping}
        />
      )}
      <div className="pagination-and-searchbar-container">
        <div className="pagination-buttons-container">
          <button
            className={`pagination-button ${
              !table.getCanPreviousPage() && "disabled"
            }`}
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.setPageIndex(0)}
          >
            <i className="fa-solid fa-angles-left"></i>
          </button>
          <button
            className={`pagination-button ${
              !table.getCanPreviousPage() && "disabled"
            }`}
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            <i className="fa-solid fa-angle-left"></i>
          </button>

          <strong>
            {table.getState().pagination.pageIndex + 1} of{" "}
            {Math.ceil(data.length / table.getState().pagination.pageSize)}
          </strong>

          <span className="go-to-input">
            <p>
              <span></span>Go to page
            </p>
            <input
              type="number"
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
            />
          </span>
          <select
            className="select-page-rows"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>

          <button
            className={`pagination-button ${
              !table.getCanNextPage() && "disabled"
            }`}
            disabled={!table.getCanNextPage()}
            onClick={() => {
              table.nextPage();
            }}
          >
            <i className="fa-solid fa-angle-right"></i>
          </button>
          <button
            className={`pagination-button ${
              !table.getCanNextPage() && "disabled"
            }`}
            disabled={!table.getCanNextPage()}
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          >
            <i className="fa-solid fa-angles-right"></i>
          </button>
        </div>
        <div className="table-search-bar">
          <div className="icon"></div>
          <input
            className="input"
            type="search"
            placeholder="Search in table..."
            value={filtering}
            onChange={(e) => setFiltering(e.target.value)}
          />
        </div>
      </div>

      <div className="table-container">
        <table
          className="table relative"
          cellSpacing={0}
          style={{
            width: `${
              table.getTotalSize() + (table.getAllLeafColumns().length + 1) * 3 // I got better result when I added this which is related with the tr css borders !
            }px`,
          }}
        >
          <thead className=" table-head">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className="table-head-row" key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                    <th
                        className="table-head-cell"
                        key={header.id}
                        data-column-name={header.id}
                        colSpan={header.colSpan}
                        style={{width: header.getSize()}}
                    >
                      {header.column.getCanResize() ? (
                          <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              className={`resize-div ${
                                  header.column.getIsResizing() ? "isResizing" : ""
                              }`}
                          />
                      ) : null}
                      {header.isPlaceholder ? null : (
                          <div
                              className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                              onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                                columnMapping[header.id] || header.column.columnDef.header,
                                header.getContext()
                            )}
                            {{
                              asc: ' 🔼',
                              desc: ' 🔽',
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                      )}
                    </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className="table-body">
          {table.getRowModel().rows.map((row) => (
              <tr className={`table-body-row`} key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const isDone =
                      cell.column.id === "status" && cell.getValue() === "DONE";

                  return (
                      <td
                          id={cell.id}
                          className={`table-body-cell ${isDone ? "done-cell" : ""}`}
                          key={cell.id}
                          onClick={() => handleCellClick(cell)}
                          style={{width: cell.column.getSize()}}
                      >
                        <div>
                          {cell.column.id === "file" ? (
                              row.original.file ? ( // If the column is "file", render a link
                            <a
                              href={row.original.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#007bff",
                                textDecoration: "underline",
                              }}
                            >
                              View file
                            </a>
                          ) : (
                            <p>No file</p>
                          )
                        ) : (
                          // Render other cells as usual
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
